/* eslint-disable react-hooks/exhaustive-deps */
// /* eslint-disable react-hooks/exhaustive-deps */
import { HubConnectionState } from '@microsoft/signalr';
import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useEffectOnce } from 'react-use';
import { Modal as RSModal } from 'reactstrap';
import { BulkEditStatus } from 'src/components/common/BulkActions/BulkEditStatus';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import {
  BulkEditStage,
  useBulkEditHubContext,
} from 'src/contexts/BulkEditHubContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { EventMapContextProvider } from 'src/contexts/EventMapContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { GenericDialogProps } from 'src/core/interim/dialogs/GenericDialog';
import { Stack } from 'src/core/ui';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { useSearchV2DataRefetch } from 'src/hooks/useSearchV2DataRefetch';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { CancellableFormHeader } from 'src/modals/common/CancellableFormHeader';
import { ConnectedEventEntityHeader } from 'src/modals/common/EventEntityHeader';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { Modal, ModalBody, ModalFooter } from 'src/modals/Modal';
import { ModalBodyDataContainer } from 'src/modals/Modal/Modal.styled';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  BulkActionType,
  BulkEditPreviewWithDetails,
  BulkEditProgress,
  BulkEditStep,
  BulkListingPriceCalculationResult,
  Event,
  Feature,
  Listing,
  ListingDetails,
  ListingDetailsAutoPricingSectionUpdatesV2,
} from 'src/WebApiController';

import { BulkEditFooter } from '../common/BulkEditFooter';
import { BulkEditHeader } from '../common/BulkEditHeader';
import {
  AutoPricingSettingsInput,
  PricingSettingsUpdateKey,
} from './BulkEditAutoPricingSettingsDialog.types';
import {
  generateDefaultSettings,
  getAutoPricingSettingsUpdateInput,
  getSettingsInputChanged,
  santizedPricingSettingsUpdateInput,
} from './BulkEditAutoPricingSettingsDialog.utils';
import { AutoPricingSettings } from './components/AutoPricingSettings';
import { BulkEditAutoPricingSettingsSidePanel } from './components/BulkEditAutoPricingSettingsSidePanel/BulkEditAutoPricingSettingsSidePanel';
import { PricingSettingsPreview } from './PricingSettingsPreview';

export type BulkEditAutoPricingSettingsDialogProps = ComponentProps<
  typeof RSModal
> & {
  updateKey: string;
  isLoading?: boolean;
  listings?: Listing[] | null;
  selectedListingIds?: number[];
  event?: Event;
  canUseZoneMap?: boolean;
  multiEvents?: boolean;
  onOkay: (
    input: ListingDetailsAutoPricingSectionUpdatesV2 | null,
    supportBackgroundProcess?: boolean,
    onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
    preview?: BulkEditPreviewWithDetails
  ) => void;
  onCancel?: () => void;
  onPreviewCalculated?: (
    result: BulkListingPriceCalculationResult | null
  ) => void;
};

export const BulkEditAutoPricingSettingsDialog: React.FC<
  BulkEditAutoPricingSettingsDialogProps
> = ({ listings, selectedListingIds, event, ...rest }) => {
  const { loginContext } = useAppContext();
  const hasAllowSeatScoreCompListingModeFeature = useUserHasFeature(
    Feature.AllowSeatScoreCompListingMode
  );
  const selectedListings = useMemo(() => {
    const flattenedListings = listings?.flatMap((l) => flattenListingGroup(l));
    return (
      flattenedListings
        ?.filter((l) => l.isFull && selectedListingIds?.includes(l.id))
        .map((l) => l as Listing) ?? []
    );
  }, [listings, selectedListingIds]);

  const isMultiEvent = useMemo(() => rest.multiEvents, [rest.multiEvents]);

  const settingsInput = useMemo(
    () =>
      generateDefaultSettings(
        selectedListings,
        Boolean(isMultiEvent),
        // The event is only provided when all selected events share a venue config
        event != null
      ),
    [selectedListings, isMultiEvent, event]
  );

  const defaultValues = useMemo(
    () =>
      getAutoPricingSettingsUpdateInput(
        settingsInput,
        loginContext?.user?.activeAccount?.currencyCode ?? null,
        hasAllowSeatScoreCompListingModeFeature
      ),
    [
      settingsInput,
      loginContext?.user?.activeAccount?.currencyCode,
      hasAllowSeatScoreCompListingModeFeature,
    ]
  );
  const methods = useForm<ListingDetailsAutoPricingSectionUpdatesV2>({
    defaultValues,
  });

  return (
    <FormProvider {...methods}>
      <EventMapContextProvider event={event}>
        <BulkEditAutoPricingSettingsDialogContent
          listings={listings}
          selectedListings={selectedListings}
          settingsInput={settingsInput}
          defaultInput={defaultValues}
          event={event}
          {...rest}
        />
      </EventMapContextProvider>
    </FormProvider>
  );
};

function BulkEditAutoPricingSettingsDialogContent({
  updateKey,
  isLoading,
  event,
  onOkay,
  onCancel,
  settingsInput,
  defaultInput,
  canUseZoneMap,
  selectedListings,
  listings,
  ...rest
}: BulkEditAutoPricingSettingsDialogProps & {
  selectedListings: Listing[];
  settingsInput: AutoPricingSettingsInput;
  defaultInput: ListingDetailsAutoPricingSectionUpdatesV2;
}) {
  const { setActivePosEntity } = useActivePosEntityContext<ListingDetails>();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { setSelectionMode } = useMultiSelectionContext();
  const { setProgress, setPreview, setMainDialogOpened } =
    useBulkEditHubContext();
  const { searchV2DataDelayedRefetch } = useSearchV2DataRefetch();

  const [changedFields, setChangedFileds] =
    useState<Partial<Record<PricingSettingsUpdateKey, boolean | undefined>>>();
  const { handleSubmit, watch, reset, formState } =
    useFormContext<ListingDetailsAutoPricingSectionUpdatesV2>();

  const input = watch();

  const onClose = useCallback(
    async (
      newProgress?: BulkEditProgress,
      closeMainDialogForPopover?: boolean
    ) => {
      if (newProgress?.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        setActivePosEntity(0);

        await searchV2DataDelayedRefetch();
        await refreshExpandedListItems();
        setSelectionMode(undefined);
        if (closeMainDialogForPopover) {
          setMainDialogOpened(false);
        } else {
          setProgress(undefined);
          setPreview(undefined);
        }
        setIsRefreshing(false);
        // progress kept to allow the BulkEditStatusPopover to show the result
      }
      onCancel?.();
    },
    [
      onCancel,
      refreshExpandedListItems,
      searchV2DataDelayedRefetch,
      setActivePosEntity,
      setMainDialogOpened,
      setPreview,
      setProgress,
      setSelectionMode,
    ]
  );

  const onBulkEditDone = useCallback(
    async (doneProgress: BulkEditProgress, finalErrors: string[]) => {
      if (finalErrors.length === 0 && doneProgress.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        onClose(doneProgress, false);
      }
    },
    [onClose]
  );

  const { bulkEditHub, progress, preview, stage, initJob } = useBulkEditHub(
    ActionOutboxEntityType.Listing,
    BulkActionType.UpdateAutoPricingSettings,
    updateKey,
    onBulkEditDone
  );

  const hasBackgroundBulkEditFeature =
    bulkEditHub?.state === HubConnectionState.Connected;

  const onSubmitHandler = useCallback(
    (onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void) => {
      if (onPreviewReceived) {
        onOkay(null, false, onPreviewReceived);
      } else {
        handleSubmit((i) => {
          onOkay(
            santizedPricingSettingsUpdateInput(i, changedFields),
            hasBackgroundBulkEditFeature,
            undefined,
            preview
          );
        })();
      }
    },
    [onOkay, handleSubmit, changedFields, hasBackgroundBulkEditFeature, preview]
  );

  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const changed = getSettingsInputChanged(
      input,
      formState.defaultValues as ListingDetailsAutoPricingSectionUpdatesV2,
      settingsInput
    );

    setChangedFileds(changed);
  }, [
    JSON.stringify(input),
    JSON.stringify(formState.defaultValues),
    JSON.stringify(settingsInput),
  ]);

  const hasChanges = useMemo(
    () => Object.values(changedFields ?? {}).some((value) => !!value),
    [changedFields]
  );

  const submittButtonRef = useRef<HTMLButtonElement>(null);

  const onUpdateChangedFields = useCallback(
    (
      values: Partial<Record<PricingSettingsUpdateKey, boolean | undefined>>
    ) => {
      setChangedFileds((prev) => ({ ...prev, ...values }));
    },
    [setChangedFileds]
  );

  return (
    <>
      <BulkEditAutoPricingSettingsModalOrDialog
        {...rest}
        event={event}
        selectedListings={selectedListings}
        size={'xl'}
        stage={stage}
        onPreviewCalculated={rest.onPreviewCalculated}
        header={
          <BulkEditHeader
            headerText={<Content id={ContentId.AutopricingSettings} />}
          />
        }
        onOpened={() => {
          reset(defaultInput);
          initJob();
        }}
        onKeyUp={(e) => {
          if (submittButtonRef.current && e.key === 'Enter' && hasChanges) {
            submittButtonRef.current.click();
          }
        }}
        onClose={onClose}
        onClosed={() => {
          setMainDialogOpened(false);
          if (progress) {
            setSelectionMode(undefined);
          }

          rest.onClosed?.();
        }}
        footer={
          isRefreshing ? null : (
            <BulkEditFooter
              entityType={ActionOutboxEntityType.Listing}
              isLoading={isLoading}
              hasBackgroundBulkEditFeature={hasBackgroundBulkEditFeature}
              onClose={onClose}
              onSubmit={onSubmitHandler}
              disabled={!hasChanges}
              submittButtonRef={submittButtonRef}
            />
          )
        }
        onCancel={isLoading ? undefined : onCancel}
      >
        <BulkEditStatus
          entityType={ActionOutboxEntityType.Listing}
          isLoading={isRefreshing || isLoading}
          updateKey={updateKey}
          height="fit"
          entityUpdatesPreview={
            <PricingSettingsPreview
              settingsInput={settingsInput}
              defaultValues={
                formState.defaultValues as ListingDetailsAutoPricingSectionUpdatesV2
              }
              updates={input}
              changedFields={changedFields}
              updateChangedFields={onUpdateChangedFields}
            />
          }
        >
          <Stack direction="column" width="full" gap="s">
            <AutoPricingSettings
              settingsInput={settingsInput}
              canUseZoneMap={canUseZoneMap}
            />
          </Stack>
        </BulkEditStatus>
      </BulkEditAutoPricingSettingsModalOrDialog>
    </>
  );
}

const BulkEditAutoPricingSettingsModalOrDialog = ({
  selectedListings,
  event,
  multiEvents = false,
  stage,
  header,
  footer,
  children,
  onClose,
  onPreviewCalculated,
  ...rest
}: GenericDialogProps & {
  selectedListings: Listing[];
  event: Event | null | undefined;
  multiEvents?: boolean;
  stage: BulkEditStage;
  onClose: () => void;
  onPreviewCalculated?: (
    result: BulkListingPriceCalculationResult | null
  ) => void;
}) => {
  const [delayLoaded, setDelayLoaded] = useState<boolean>(false);

  // Trick to delay showing side panel to avoid modal bouncing left and right
  useEffectOnce(() => {
    setTimeout(() => {
      setDelayLoaded(true);
    }, 50);
  });

  // We don't want to show side pannel for multi-events,
  // but add this logic behind ulkEditPricingSettingsUpdate feature
  const showSidePanel = useMemo(
    () =>
      Boolean(event?.viagId && stage === BulkEditStage.Idle && delayLoaded) &&
      !multiEvents,
    [event?.viagId, stage, delayLoaded, multiEvents]
  );

  return (
    <Modal {...rest} size={'slide-in'} clickOutsideToClose>
      <Stack
        direction="row"
        height="full"
        width="full"
        style={{ overflowX: 'auto' }}
      >
        {event?.viagId && (
          <BulkEditAutoPricingSettingsSidePanel
            event={event}
            selectedListings={selectedListings}
            show={showSidePanel}
            onPreviewCalculated={onPreviewCalculated}
          />
        )}
        <Stack direction="column" height="full">
          <CancellableFormHeader onClose={onClose}>
            <ConnectedEventEntityHeader title={header} />
          </CancellableFormHeader>

          <ModalBody>
            <ModalBodyDataContainer>{children}</ModalBodyDataContainer>
          </ModalBody>

          <ModalFooter>{footer}</ModalFooter>
        </Stack>
      </Stack>
    </Modal>
  );
};
